import PropTypes from 'prop-types';
import React, { Component } from 'react';
import FileSaver from 'file-saver';
import { base64StringToBlob } from 'blob-util';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import config from '../../../../core/config/configureSettings';
import reportAPI from '../api/ReportAPI';
import { fetchDownloadReportResultActionCreator } from '../redux/actions/ReportsActions';
import { getDownloadReportResult } from '../redux/selectors/ReportsSelectors';
import capeNotificationContainer from '../../../common/CapeNotificationContainer';
import { DOWNLOAD_FAILURE_MESSAGE, getDownloadFailureMessage } from '../ReportDownloadMessages';

export class ReportContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      readyFiles: [],
      streamedDownloadStarted: false,
      streamedFailureMessage: null,
    };
  }

  componentDidMount() {
    const { reportName, execId } = this.props.match.params;
    if (config.useStreamedReportDownload) {
      this.pendingStreamedDownload = this.startStreamedDownload(reportName, execId);
      return;
    }
    this.props.fetchDownloadReportResultActionCreator(reportName, execId, 1);
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.downloadFrame && this.downloadFrame.parentNode) {
      this.downloadFrame.parentNode.removeChild(this.downloadFrame);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.downloadReportResult !== this.props.downloadReportResult
      && this.isCurrentDownload(this.props.downloadReportResult)
      && this.props.downloadReportResult.status === 'success'
      && this.props.downloadReportResult.zip) {
      let batchNoResp = this.props.downloadReportResult.asyncReportMetaData.batchNo;
      const batchCountResp = this.props.downloadReportResult.asyncReportMetaData.batchCount;
      const batchDetailsOnFileName = batchCountResp === 1 ? '' : `_${(`0${batchNoResp}`).slice(-2)}_${(`0${batchCountResp}`).slice(-2)}`;
      const fileName = `${this.props.match.params.reportName}_${moment(Date.now()).format('YYYYMMDDHHmm')}${batchDetailsOnFileName}.zip`;
      const blob = base64StringToBlob(this.props.downloadReportResult.zip, 'octet/stream');
      FileSaver.saveAs(blob, fileName);
      batchNoResp += 1;
      if (batchNoResp <= batchCountResp) {
        this.props.fetchDownloadReportResultActionCreator(this.props.match.params.reportName, this.props.match.params.execId, batchNoResp);
      }
      const readyFiles = Array.from(prevState.readyFiles);
      readyFiles.push(fileName);
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        readyFiles,
      });
    }
  }

  /**
   * A hidden iframe rather than window.location: if the session has expired the response is an
   * authentication redirect, which would replace the application in the tab. The browser writes
   * the report straight to disk, so nothing passes through the page.
   */
  startStreamedDownload(reportName, execId) {
    return reportAPI.checkStreamedDownload(reportName, execId)
      .then(() => {
        if (this.unmounted) return;
        this.downloadFrame = document.createElement('iframe');
        this.downloadFrame.style.display = 'none';
        this.downloadFrame.src = reportAPI.streamedDownloadReportResultURL(reportName, execId);
        document.body.appendChild(this.downloadFrame);
        this.setState({ streamedDownloadStarted: true });
      })
      .catch((error) => {
        if (this.unmounted) return;
        this.setState({ streamedFailureMessage: getDownloadFailureMessage(error) });
      });
  }

  isCurrentDownload(result) {
    const { reportName, execId } = this.props.match.params;
    return result && result.request
      && result.request.reportName === reportName && result.request.execId === execId;
  }

  render() {
    if (config.useStreamedReportDownload) {
      const streamedLines = [];
      if (this.state.streamedFailureMessage) {
        streamedLines.push(this.state.streamedFailureMessage);
      } else if (this.state.streamedDownloadStarted) {
        streamedLines.push('Your download has started.');
        streamedLines.push('A large report can take several minutes. Your browser shows the progress.');
      } else {
        streamedLines.push('Preparing your download...');
      }

      return (
        <div className="report-message-download">
          {/* eslint-disable-next-line react/no-array-index-key */}
          {streamedLines.map((line, key) => <p key={key}>{line}</p>)}
        </div>
      );
    }

    const { downloadReportResult } = this.props;
    const { readyFiles } = this.state;
    const statusMessageLines = [];
    readyFiles.forEach(f => statusMessageLines.push(`${f} ready.`));
    if (this.isCurrentDownload(downloadReportResult) && downloadReportResult.status === 'failure') {
      statusMessageLines.push(downloadReportResult.failureMessage || DOWNLOAD_FAILURE_MESSAGE);
    } else if (this.isCurrentDownload(downloadReportResult) && downloadReportResult.asyncReportMetaData.batchCount > 0) {
      if (downloadReportResult.asyncReportMetaData.batchCount === 1) {
        statusMessageLines.push('Report content ready.');
      } else if (downloadReportResult.asyncReportMetaData.batchNo < downloadReportResult.asyncReportMetaData.batchCount) {
        statusMessageLines.push(`Fetching batch ${downloadReportResult.asyncReportMetaData.batchNo + 1} of ${downloadReportResult.asyncReportMetaData.batchCount}...`);
      } else {
        statusMessageLines.push('Report content ready.');
      }
    } else {
      statusMessageLines.push('Fetching report...');
    }

    return (
      <div className="report-message-download">
        {/* eslint-disable-next-line react/no-array-index-key */}
        {statusMessageLines.map((i, key) => <p key={key}>{i}</p>)}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    downloadReportResult: getDownloadReportResult(state),
  };
}

const mapDispatchToProps = dispatch => bindActionCreators({
  fetchDownloadReportResultActionCreator,
}, dispatch);

ReportContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      reportName: PropTypes.string.isRequired,
      execId: PropTypes.string.isRequired,
    }),
  }),
  fetchDownloadReportResultActionCreator: PropTypes.func,
  downloadReportResult: PropTypes.objectOf(PropTypes.shape),
};

export const ConnectedReportContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ReportContainer);

export default capeNotificationContainer(ConnectedReportContainer);
